import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserProvider';
import PageHeader from '../components/PageHeader';
import defaultArt from '../assets/images/dj_kip_art.png';
import HorizontalSection from '../components/sections/HorizontalSection';
import SetMobileCard from '../components/SetMobileCard';
import TrackMobileCard from '../components/TrackMobileCard';
import ArtistCard from '../components/ArtistCard';
import LabelCard from '../components/LabelCard';
import CuratorCard from '../components/CuratorCard';
import GenreCard from '../components/GenreCard';
import OnboardingModal from '../components/OnboardingModal';
import { Typography, Box } from '@mui/material';
import { Favorite } from '@mui/icons-material';
import { useApi } from '../context/ApiProvider';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showOnboarding, setShowOnboarding] = React.useState(false);

  useEffect(() => {
    if (
      user &&
      (user.completed_onboarding === false ||
        user.completed_onboarding === null)
    ) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [user]);

  useEffect(() => {
    const next = localStorage.getItem('next');
    if (next) {
      localStorage.removeItem('next');
      navigate(next, { replace: true });
    }
  }, []);

  const [genreData, setGenreData] = useState([]);
  const [loadingGenres, setLoadingGenres] = useState(true);
  const api = useApi();

  // Genre permalinks to fetch
  const genrePermalinks = [
    'house',
    'deep-house',
    'progressive-house',
    'melodic-house-techno',
    'afro-house',
    'indie-dance',
  ];

  // Fetch genre data from API (staggered: 600ms initial delay, 150ms between each)
  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoadingGenres(true);
      const results = [];
      try {
        for (let i = 0; i < genrePermalinks.length; i++) {
          if (i > 0) await new Promise((r) => setTimeout(r, 150));
          try {
            const response = await api.get(`/genres/${genrePermalinks[i]}`);
            if (response.ok)
              results.push({ ...response.body, art_url: defaultArt });
          } catch (_) {
            results.push(null);
          }
        }
        setGenreData(results.filter(Boolean));
      } catch (error) {
        console.error('Error fetching genre data:', error);
        // Fallback to basic genre data if API fails
        const fallbackGenres = genrePermalinks.map((permalink) => ({
          name: permalink
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          permalink: permalink,
          track_count: 0,
          set_count: 0,
          artist_count: 0,
          art_url: defaultArt,
        }));
        setGenreData(fallbackGenres);
      } finally {
        setLoadingGenres(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [api]);

  return (
    <>
      {user &&
        (user.completed_onboarding === false ||
          user.completed_onboarding === null) && (
          <OnboardingModal
            open={showOnboarding}
            onClose={() => setShowOnboarding(false)}
          />
        )}
      <PageHeader backgroundArt={defaultArt} minHeight={350}>
        <Typography variant="h3" fontWeight="bold" mt={6}>
          Welcome to{' '}
          <Typography
            variant="h3"
            component="p"
            color="primary"
            fontWeight="bold"
            sx={{
              display: 'inline-block',
              '&::first-letter': {
                fontFamily: 'Pixel',
              },
            }}
          >
            KIP
          </Typography>
        </Typography>
        <Typography mt={2}>
          Listen to sets and save tracks to your profile when you{' '}
          <Favorite sx={{ verticalAlign: 'bottom' }} />
        </Typography>
        <Typography mt={1}>
          Know what track is playing without Shazam
        </Typography>
        <Typography mt={1}>
          Get notifications when IDs are released by the artist
        </Typography>
      </PageHeader>

      <Box>
        <Typography></Typography>
      </Box>

      <Box sx={{ padding: { xs: '20px', lg: '30px 0px' } }}>
        <HorizontalSection
          dataFrom={'/featured/sets'}
          title="Featured Sets"
          cardComponent={SetMobileCard}
          defaultOrderBy="set_meta.release_date"
          seeMoreUrl="/sets"
          fetchDelay={0}
          cardStyles={{
            flexShrink: 0,
            width: { xs: '100vw', sm: '400px' },
            maxWidth: '400px',
            minWidth: '300px',
          }}
          extraCardProps={{
            showGenreLink: true,
            extraSmall: false,
          }}
        />

        {/* Genre Cards Section */}
        <HorizontalSection
          title="Explore Genres"
          cardComponent={({ item }) => <GenreCard genre={item} />}
          items={genreData}
          loading={loadingGenres}
          cardStyles={{
            flexShrink: 0,
            width: { xs: '280px', sm: '320px' },
            maxWidth: '320px',
            minWidth: '280px',
          }}
        />

        <HorizontalSection
          dataFrom={'/featured/tracks'}
          title="Newest Tracks"
          cardComponent={TrackMobileCard}
          defaultOrderBy="track_meta.release_date"
          seeMoreUrl="/tracks"
          fetchDelay={400}
          cardStyles={{
            flexShrink: 0,
            width: { xs: '100vw', sm: '400px' },
            maxWidth: '400px',
            minWidth: '300px',
          }}
          extraCardProps={{
            showGenreLink: true,
            extraSmall: false,
          }}
        />
        <HorizontalSection
          dataFrom={'/featured/artists'}
          title="Featured Artists"
          cardComponent={ArtistCard}
          seeMoreUrl="/artists"
          showDetailedInfo={false}
          fetchDelay={800}
        />
        <HorizontalSection
          dataFrom={'/featured/labels'}
          title="Featured Labels"
          cardComponent={LabelCard}
          seeMoreUrl="/labels"
          showDetailedInfo={false}
          fetchDelay={1200}
        />
        <HorizontalSection
          dataFrom={'/featured/festivals'}
          title="Featured Festivals"
          cardComponent={CuratorCard}
          showDetailedInfo={false}
          fetchDelay={1600}
        />
        <HorizontalSection
          dataFrom={'/featured/venues'}
          title="Featured Venues"
          cardComponent={CuratorCard}
          showDetailedInfo={false}
          fetchDelay={2000}
        />
      </Box>
    </>
  );
};

export default Home;
